import { useState } from 'react'
import { Loader2, Send } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'

import { useSendMessage } from '@/features/messaging/hooks'

// Inline reply box for an open customer conversation: free-text send with
// Cmd/Ctrl+Enter, without the template picker the compose dialog offers.
export function ReplyComposer({
  customerId,
  dealId,
  workOrderId,
}: {
  customerId: string
  /** Optionally link sends to a deal / work order (resolved server-side). */
  dealId?: string
  workOrderId?: string
}) {
  const sendMutation = useSendMessage()
  const [body, setBody] = useState('')
  const [error, setError] = useState<string | null>(null)

  const handleSend = () => {
    setError(null)
    // The backend resolves exactly one context id per send (customer →
    // deal → work order), so pass only the most specific one.
    const context = workOrderId
      ? { work_order_id: workOrderId }
      : dealId
        ? { deal_id: dealId }
        : { customer_id: customerId }
    sendMutation.mutate(
      { ...context, body },
      {
        onSuccess: () => setBody(''),
        onError: (err) => setError(err instanceof Error ? err.message : 'Failed to send message'),
      },
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Write a reply…"
        className="min-h-20"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault()
            if (body.trim()) handleSend()
          }
        }}
      />
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={handleSend}
          disabled={sendMutation.isPending || body.trim().length === 0}
        >
          {sendMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Send className="mr-2 h-4 w-4" />
          )}
          Send
        </Button>
      </div>
    </div>
  )
}
